import React, { useState, useEffect } from 'react';
import {
  ArrowLeft,
  CheckCircle,
  XCircle,
  Clock,
  RefreshCw,
  Building2,
  CreditCard,
  DollarSign,
  Calendar,
  AlertCircle
} from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import adminService, { Settlement } from '../../services/admin/adminService';
import { formatCurrency } from '../../utils/formatters';
import { getImageUrl } from '../../utils/imageHelper';

const SettlementDetails: React.FC = () => {
  const navigate = useNavigate();
  const { settlementId } = useParams<{ settlementId: string }>();

  const [settlement, setSettlement] = useState<Settlement | null>(null);
  const [loading, setLoading] = useState(true);

  // Modals
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);

  // Form states
  const [approvalNotes, setApprovalNotes] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    if (settlementId) {
      loadSettlement();
    }
  }, [settlementId]);

  const loadSettlement = async () => {
    try {
      setLoading(true);
      const data = await adminService.getSettlementById(Number(settlementId));
      setSettlement(data);
    } catch (error: any) {
      toast.error('Failed to load settlement details');
      navigate('/admin/wallet');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!settlement) return;

    try {
      setProcessing(true);
      await adminService.approveSettlement({
        settlement_id: settlement.settlement_id,
        notes: approvalNotes
      });
      toast.success('Settlement approved successfully');
      setShowApproveModal(false);
      setApprovalNotes('');
      await loadSettlement();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to approve settlement');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!settlement) return;

    try {
      setProcessing(true);
      await adminService.rejectSettlement({
        settlement_id: settlement.settlement_id,
        rejection_reason: rejectionReason
      });
      toast.success('Settlement rejected');
      setShowRejectModal(false);
      setRejectionReason('');
      await loadSettlement();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to reject settlement');
    } finally {
      setProcessing(false);
    }
  };



  const getStatusBadge = (status: string) => {
    const badges = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      processing: 'bg-blue-100 text-blue-800',
      completed: 'bg-primary-100 text-primary-800',
      failed: 'bg-red-100 text-red-800'
    };
    return badges[status as keyof typeof badges] || 'bg-gray-100 text-gray-800';
  };

  const getStatusIcon = (status: string) => {
    const icons = {
      pending: <Clock className="text-yellow-600" size={20} />,
      approved: <CheckCircle className="text-green-600" size={20} />,
      rejected: <XCircle className="text-red-600" size={20} />,
      processing: <RefreshCw className="text-blue-600" size={20} />,
      completed: <CheckCircle className="text-primary-600" size={20} />,
      failed: <XCircle className="text-red-600" size={20} />
    };
    return icons[status as keyof typeof icons] || <AlertCircle className="text-gray-600" size={20} />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <RefreshCw className="animate-spin text-primary-500" size={48} />
      </div>
    );
  }

  if (!settlement) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <AlertCircle size={64} className="mx-auto text-gray-300 mb-4" />
          <p className="text-secondary-600">Settlement not found</p>
          <button
            onClick={() => navigate('/admin/wallet')}
            className="mt-4 px-6 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600"
          >
            Back to Wallet
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <button
            onClick={() => navigate('/admin/wallet')}
            className="flex items-center gap-2 text-secondary-600 hover:text-secondary-900 mb-4"
          >
            <ArrowLeft size={20} />
            Back to Settlements
          </button>
          <h1 className="text-3xl font-bold text-secondary-900">Settlement Details</h1>
          <p className="text-secondary-600 mt-1">{settlement.settlement_number}</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={loadSettlement}
            className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
          >
            <RefreshCw size={18} />
            Refresh
          </button>
        </div>
      </div>

      {/* Status Banner */}
      <div className={`rounded-lg p-6 ${getStatusBadge(settlement.status).replace('text-', 'bg-').replace('-800', '-50')} border-l-4 ${getStatusBadge(settlement.status).replace('bg-', 'border-').replace('-100', '-500')}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            {getStatusIcon(settlement.status)}
            <div>
              <h3 className="text-lg font-semibold text-secondary-900">
                Settlement Status: <span className="capitalize">{settlement.status}</span>
              </h3>
              <p className="text-sm text-secondary-600 mt-1">
                Created on {new Date(settlement.created_at).toLocaleString()}
              </p>
            </div>
          </div>
          <span className={`px-4 py-2 text-sm font-semibold rounded-full ${getStatusBadge(settlement.status)}`}>
            {settlement.status.toUpperCase()}
          </span>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Settlement Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Amount Details */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-xl font-semibold text-secondary-900 mb-6 flex items-center gap-2">
              <DollarSign size={24} className="text-primary-600" />
              Amount Details
            </h3>
            <div className="grid grid-cols-3 gap-6">
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm text-secondary-600 mb-1">Settlement Amount</p>
                <p className="text-2xl font-bold text-secondary-900">
                  {formatCurrency(settlement.amount, settlement.currency_code, 'en-IN', settlement.currency_symbol)}
                </p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm text-secondary-600 mb-1">Charges</p>
                <p className="text-2xl font-bold text-red-600">
                  {formatCurrency(settlement.charges, settlement.currency_code, 'en-IN', settlement.currency_symbol)}
                </p>
              </div>
              <div className="bg-primary-50 p-4 rounded-lg">
                <p className="text-sm text-secondary-600 mb-1">Net Amount</p>
                <p className="text-2xl font-bold text-primary-600">
                  {formatCurrency(settlement.net_amount, settlement.currency_code, 'en-IN', settlement.currency_symbol)}
                </p>
              </div>
            </div>
          </div>

          {/* Business Information */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-xl font-semibold text-secondary-900 mb-6 flex items-center gap-2">
              <Building2 size={24} className="text-primary-600" />
              Business Information
            </h3>
            <div className="flex items-center gap-4 mb-6">
              {settlement.business_logo ? (
                <img
                  src={getImageUrl(settlement.business_logo) || ''}
                  alt={settlement.business_name}
                  className="w-16 h-16 rounded-lg object-cover border border-gray-200"
                />
              ) : (
                <div className="w-16 h-16 rounded-lg bg-primary-100 flex items-center justify-center">
                  <Building2 size={32} className="text-primary-600" />
                </div>
              )}
              <div>
                <h4 className="text-lg font-semibold text-secondary-900">{settlement.business_name}</h4>
                <div className="flex items-center gap-2 mt-1">
                  <p className="text-sm text-secondary-600">Business ID: {settlement.business_id}</p>
                  {settlement.business_country && (
                    <>
                      <span className="text-secondary-300">•</span>
                      <p className="text-sm text-secondary-600">{settlement.business_country}</p>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Bank Details */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-xl font-semibold text-secondary-900 mb-6 flex items-center gap-2">
              <CreditCard size={24} className="text-primary-600" />
              Bank Account Details
            </h3>
            <div className="grid grid-cols-2 gap-6">
              <div>
                <p className="text-sm text-secondary-600 mb-1">Account Holder Name</p>
                <p className="text-lg font-medium text-secondary-900">{settlement.account_holder_name}</p>
              </div>
              <div>
                <p className="text-sm text-secondary-600 mb-1">Bank Name</p>
                <p className="text-lg font-medium text-secondary-900">{settlement.bank_name}</p>
              </div>
              <div>
                <p className="text-sm text-secondary-600 mb-1">Account Number</p>
                <p className="text-lg font-medium text-secondary-900 font-mono">
                  {settlement.account_number.replace(/(\d{4})/g, '$1 ').trim()}
                </p>
              </div>

            </div>
          </div>

          {/* Processing Information */}
          {(settlement.gateway_settlement_id || settlement.gateway_response || settlement.failure_reason) && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-xl font-semibold text-secondary-900 mb-6">Processing Information</h3>
              <div className="space-y-4">
                {settlement.gateway_settlement_id && (
                  <div>
                    <p className="text-sm text-secondary-600 mb-1">Gateway Settlement ID</p>
                    <p className="text-md font-medium text-secondary-900 font-mono">{settlement.gateway_settlement_id}</p>
                  </div>
                )}
                {settlement.processed_at && (
                  <div>
                    <p className="text-sm text-secondary-600 mb-1">Processed At</p>
                    <p className="text-md font-medium text-secondary-900">{new Date(settlement.processed_at).toLocaleString()}</p>
                  </div>
                )}
                {settlement.failure_reason && (
                  <div>
                    <p className="text-sm text-secondary-600 mb-1">Failure Reason</p>
                    <p className="text-md font-medium text-red-600">{settlement.failure_reason}</p>
                  </div>
                )}
                {settlement.gateway_response && (
                  <div>
                    <p className="text-sm text-secondary-600 mb-1">Gateway Response</p>
                    <pre className="text-xs bg-gray-50 p-3 rounded-lg overflow-auto max-h-40 text-secondary-900">
                      {JSON.stringify(settlement.gateway_response, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Right Column - Timeline & Actions */}
        <div className="space-y-6">
          {/* Settlement Timeline */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-xl font-semibold text-secondary-900 mb-6 flex items-center gap-2">
              <Calendar size={24} className="text-primary-600" />
              Timeline
            </h3>
            <div className="space-y-4">
              <div className="flex gap-3">
                <div className="flex flex-col items-center">
                  <div className="w-8 h-8 rounded-full bg-primary-500 flex items-center justify-center">
                    <CheckCircle size={16} className="text-white" />
                  </div>
                  <div className="w-0.5 h-full bg-gray-200 mt-2"></div>
                </div>
                <div className="flex-1 pb-6">
                  <p className="font-semibold text-secondary-900">Created</p>
                  <p className="text-sm text-secondary-600">{new Date(settlement.created_at).toLocaleString()}</p>
                </div>
              </div>

              {settlement.updated_at !== settlement.created_at && (
                <div className="flex gap-3">
                  <div className="flex flex-col items-center">
                    <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center">
                      <RefreshCw size={16} className="text-white" />
                    </div>
                    {settlement.processed_at && <div className="w-0.5 h-full bg-gray-200 mt-2"></div>}
                  </div>
                  <div className="flex-1 pb-6">
                    <p className="font-semibold text-secondary-900">Updated</p>
                    <p className="text-sm text-secondary-600">{new Date(settlement.updated_at).toLocaleString()}</p>
                  </div>
                </div>
              )}

              {settlement.processed_at && (
                <div className="flex gap-3">
                  <div className="flex flex-col items-center">
                    <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center">
                      <CheckCircle size={16} className="text-white" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-secondary-900">Processed</p>
                    <p className="text-sm text-secondary-600">{new Date(settlement.processed_at).toLocaleString()}</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Quick Info */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-xl font-semibold text-secondary-900 mb-4">Quick Info</h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-sm text-secondary-600">Type</span>
                <span className="text-sm font-semibold text-secondary-900 capitalize">{settlement.settlement_type}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-sm text-secondary-600">Bank Detail ID</span>
                <span className="text-sm font-semibold text-secondary-900">{settlement.bank_detail_id}</span>
              </div>
              {settlement.admin_id && (
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-sm text-secondary-600">Admin ID</span>
                  <span className="text-sm font-semibold text-secondary-900">{settlement.admin_id}</span>
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          {(settlement.status === 'pending' || settlement.status === 'processing') && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-xl font-semibold text-secondary-900 mb-4">Actions</h3>
              <div className="space-y-3">
                <button
                  onClick={() => setShowApproveModal(true)}
                  className="w-full px-4 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors flex items-center justify-center gap-2"
                >
                  <CheckCircle size={20} />
                  Approve Settlement
                </button>
                <button
                  onClick={() => setShowRejectModal(true)}
                  className="w-full px-4 py-3 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors flex items-center justify-center gap-2"
                >
                  <XCircle size={20} />
                  Reject Settlement
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Approve Modal */}
      {showApproveModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-xl font-semibold text-secondary-900">Approve Settlement</h3>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-secondary-700">
                Are you sure you want to approve settlement <strong>{settlement.settlement_number}</strong> for{' '}
                <strong>{formatCurrency(settlement.net_amount, settlement.currency_code, 'en-IN', settlement.currency_symbol)}</strong>?
              </p>
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  Approval Notes (Optional)
                </label>
                <textarea
                  value={approvalNotes}
                  onChange={(e) => setApprovalNotes(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="Add any notes about this approval..."
                />
              </div>
              <div className="flex gap-3">
                <button
                  onClick={handleApprove}
                  disabled={processing}
                  className="flex-1 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors disabled:opacity-50"
                >
                  {processing ? 'Processing...' : 'Confirm Approval'}
                </button>
                <button
                  onClick={() => {
                    setShowApproveModal(false);
                    setApprovalNotes('');
                  }}
                  disabled={processing}
                  className="flex-1 px-4 py-2 bg-gray-300 text-secondary-900 rounded-lg hover:bg-gray-400 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-xl font-semibold text-secondary-900">Reject Settlement</h3>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-secondary-700">
                Reject settlement <strong>{settlement.settlement_number}</strong>?
              </p>
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  Rejection Reason (Optional)
                </label>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="Explain why this settlement is being rejected..."
                />
              </div>
              <div className="flex gap-3">
                <button
                  onClick={handleReject}
                  disabled={processing}
                  className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50"
                >
                  {processing ? 'Processing...' : 'Confirm Rejection'}
                </button>
                <button
                  onClick={() => {
                    setShowRejectModal(false);
                    setRejectionReason('');
                  }}
                  disabled={processing}
                  className="flex-1 px-4 py-2 bg-gray-300 text-secondary-900 rounded-lg hover:bg-gray-400 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}


    </div>
  );
};

export default SettlementDetails;

